import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useOrg } from '../contexts/OrgContext';
import Header from '../components/Header';
import { formatType } from '../data/builtinTemplates';

export default function Admin() {
  const { membership } = useOrg();
  const [stats, setStats] = useState({ total: 0, users: 0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!membership?.org_id) return;

    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);

    async function loadStats() {
      const [totalRes, usersRes, recentRes] = await Promise.all([
        supabase
          .from('submissions')
          .select('id', { count: 'exact', head: true })
          .eq('org_id', membership.org_id)
          .gte('created_at', monthStart.toISOString()),

        supabase
          .from('organisation_members')
          .select('id', { count: 'exact', head: true })
          .eq('org_id', membership.org_id),

        supabase
          .from('submissions')
          .select('id, checklist_type, driver_name, created_at')
          .eq('org_id', membership.org_id)
          .order('created_at', { ascending: false })
          .limit(10),
      ]);

      setStats({
        total: totalRes.count ?? 0,
        users: usersRes.count ?? 0,
      });
      if (recentRes.data) setRecent(recentRes.data);
      setLoading(false);
    }

    loadStats();
  }, [membership?.org_id]);

  const adminLinks = [
    { to: '/admin/assets', initial: 'A', label: 'Assets', desc: 'Manage vehicles and rigs' },
    { to: '/admin/users', initial: 'U', label: 'Users', desc: 'Manage org members' },
    { to: '/admin/templates', initial: 'T', label: 'Templates', desc: 'Custom checklist builder' },
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      <Header title="Admin" backTo="/dashboard" />

      <div className="max-w-lg mx-auto px-4 py-5 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
            <p className="text-2xl font-bold text-white">{loading ? '—' : stats.total}</p>
            <p className="text-gray-500 text-xs mt-1">Submissions this month</p>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
            <p className="text-2xl font-bold text-white">{loading ? '—' : stats.users}</p>
            <p className="text-gray-500 text-xs mt-1">Active members</p>
          </div>
        </div>

        {/* Admin links */}
        <div className="space-y-2">
          {adminLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="flex items-center gap-4 bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-gray-600 transition-colors"
            >
              <div className="bg-orange-500/20 rounded-lg w-12 h-12 flex items-center justify-center shrink-0">
                <span className="text-orange-400 font-bold text-sm">{link.initial}</span>
              </div>
              <div>
                <p className="text-white font-medium">{link.label}</p>
                <p className="text-gray-500 text-xs">{link.desc}</p>
              </div>
              <span className="ml-auto text-gray-600">›</span>
            </Link>
          ))}
        </div>

        {/* Recent submissions */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-semibold text-base">Recent Submissions</h2>
            <Link to="/submissions" className="text-orange-400 text-sm hover:text-orange-300">
              View all
            </Link>
          </div>

          {loading ? (
            <p className="text-gray-500 text-center py-6">Loading…</p>
          ) : recent.length === 0 ? (
            <p className="text-gray-500 text-center py-6">No submissions yet</p>
          ) : (
            <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
              <div className="divide-y divide-gray-700">
                {recent.map((s) => (
                  <Link
                    key={s.id}
                    to={`/submissions/${s.id}`}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-700/30 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium truncate">
                        {formatType(s.checklist_type)}
                      </p>
                      <p className="text-gray-500 text-xs">
                        {s.driver_name} · {new Date(s.created_at).toLocaleDateString('en-AU')}
                      </p>
                    </div>
                    <span className="text-gray-600 ml-3">›</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}